import { Box, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import MDEditor from "@uiw/react-md-editor";
import { Helmet } from "react-helmet";
import { useMutation } from "urql";

import { DismissWorkspaceNotices } from "../workspaces/queries.graphql";

const PageContainer = styled(Stack)(({ theme }) => ({
  position: "relative",
  zIndex: 1,
  margin: "0 auto",
  maxWidth: 1280,
  padding: theme.spacing(24, 0),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(32, 6),
  },
  [theme.breakpoints.up("lg")]: {
    padding: theme.spacing(32, 8),
  },
}));

type TeamNoticesProps = {
  workspaceId: string;
  workspaceName: string;
  workspaceLogo: string;
  notices: string[];
};

export default function TeamNotices(props: TeamNoticesProps) {
  const { workspaceId, workspaceName, workspaceLogo, notices } = props;

  const [dismissWorkspaceNoticesResult, dismissWorkspaceNotices] = useMutation(
    DismissWorkspaceNotices,
  );

  const onAccept = () => {
    dismissWorkspaceNotices({
      dismissWorkspaceNoticesInput: { workspaceId },
    });
  };

  return (
    <Box>
      <Helmet>
        <title>{workspaceName} › Team Notices</title>
      </Helmet>
      <Box sx={{ bgcolor: "secondary.9" }}>
        <PageContainer alignItems="center">
          <Stack alignItems="center" sx={{ mx: "auto", maxWidth: 672 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                bgcolor: "secondary.7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                src={workspaceLogo}
                alt="Logo"
                sx={{
                  width: 40,
                  height: 40,
                  objectFit: "cover",
                  borderRadius: "50%",
                }}
              />
            </Box>
            <Typography
              variant="title3"
              sx={{
                mt: 6,
                fontWeight: 800,
                textAlign: "center",
                fontSize: 22,
                color: "secondary.2",
              }}
            >
              Please accept your team&apos;s notices
            </Typography>
            {notices.map((notice, idx) => (
              <Box
                key={`team-notice-${idx}`}
                sx={{
                  width: "100%",
                  mt: 6,
                  border: "1px solid",
                  borderRadius: 1,
                  borderColor: "secondary.7",
                  p: 4,
                  color: "secondary.3",
                }}
              >
                <MDEditor.Markdown
                  source={notice}
                  style={{
                    whiteSpace: "pre-wrap",
                    background: "transparent",
                    fontFamily: "inherit",
                    color: "inherit",
                  }}
                />
              </Box>
            ))}

            <Button
              sx={{
                mx: "auto",
                mt: 6,
                width: 288,
                borderRadius: 1,
                px: 4,
                py: 2,
                fontSize: 13,
                fontWeight: 600,
                color: "common.white",
                bgcolor: dismissWorkspaceNoticesResult.fetching
                  ? "secondary.6"
                  : "primary.main",
              }}
              onClick={onAccept}
              disabled={dismissWorkspaceNoticesResult.fetching}
            >
              Accept and Jump In
            </Button>
          </Stack>
        </PageContainer>
      </Box>
    </Box>
  );
}
